import { useMutation, useQuery } from '@apollo/client';
import { Badge, Box, Button, Flex, Grid, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tooltip, Tr } from '@chakra-ui/react';
import { useContext, useState } from 'react';
import { GET_UPS_PARCELS, MANUAL_SYNC, SET_PARCEL_HIDDEN_IN_APP, UPSParcelsQuery } from '../query';
import UserContext from '../../../contexts/UserContext';
import { formatUTCToLocal } from '../../../utils';
import { parcelStatusText } from '../../parcelStatus';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useSpinner } from '../../../contexts/SpinnerContext';
import { GET_PARCELS, InternalAllUPSParcelsQuery, UPSParcelList } from '../../AccessPoints/Parcels/query';

export const statusColorScheme = {
  INBOUND: 'grey',
  READY_FOR_CONSIGNEE_PICKUP: 'yellow',
  READY_FOR_UPS_PICKUP: 'yellow',
  DISPOSSESSIONS: 'green',
};

export const UPSParcels = ({ accessPointID }) => {
  const [status, setStatus] = useState(['INBOUND', 'READY_FOR_CONSIGNEE_PICKUP', 'READY_FOR_UPS_PICKUP', 'DROP_OFF']);
  const [manualSync] = useMutation(MANUAL_SYNC);
  const [setParcelHiddenInApp] = useMutation(SET_PARCEL_HIDDEN_IN_APP);
  const { user } = useContext(UserContext);
  const { toggleSpinner } = useSpinner();
  const toast = useCustomToast();
  // const { data, loading, error, refetch } = useQuery<UPSParcelsQuery>(GET_UPS_PARCELS, {
  //   variables: {
  //     accessPointID,
  //     status,
  //   },
  // });
  const { data, loading, error, refetch } = useQuery<InternalAllUPSParcelsQuery>(GET_PARCELS, {
    variables: {
      accessPointIDs: [accessPointID],
      statuses: status,
    },
  });

  const handleStatusChange = (status: string) => {
    setStatus((prevStatus) => (prevStatus.includes(status) ? prevStatus.filter((s) => s !== status) : [...prevStatus, status]));
  };

  const handleManualSync = async () => {
    toggleSpinner(true);
    try {
      await manualSync({ variables: { accessPointID } });
      toast({
        title: 'Erfolg!',
        description: 'Der Inventar-Sync wurde initialisiert. Aktualisiere die Übersicht in 30 Sekunden.',
        status: 'success',
      });
    } catch (error) {
      console.log(error);
    } finally {
      toggleSpinner(false);
    }
  };

  const handleRefresh = async () => {
    toggleSpinner(true);
    try {
      await refetch();
    } catch (error) {
      console.log(error);
    } finally {
      toggleSpinner(false);
    }
  };

  return (
    <Box borderLeftWidth="1px" borderRightWidth="1px" borderBottomWidth="1px" px="4" py="8">
      <Flex justifyContent="flex-end" alignItems="center" gap="2" mb="4">
        <Button
          size="xs"
          colorScheme="brand"
          variant={status.includes('INBOUND') ? 'solid' : 'outline'}
          onClick={() => handleStatusChange('INBOUND')}>
          INBOUND
        </Button>
        <Button
          size="xs"
          colorScheme="brand"
          variant={status.includes('READY_FOR_CONSIGNEE_PICKUP') ? 'solid' : 'outline'}
          onClick={() => handleStatusChange('READY_FOR_CONSIGNEE_PICKUP')}>
          READY_FOR_CONSIGNEE_PICKUP
        </Button>
        <Button
          size="xs"
          colorScheme="brand"
          variant={status.includes('READY_FOR_UPS_PICKUP') ? 'solid' : 'outline'}
          onClick={() => handleStatusChange('READY_FOR_UPS_PICKUP')}>
          READY_FOR_UPS_PICKUP
        </Button>
        <Button
          size="xs"
          colorScheme="brand"
          variant={status.includes('AWAITS_DRIVER_PICKUP') ? 'solid' : 'outline'}
          onClick={() => handleStatusChange('AWAITS_DRIVER_PICKUP')}>
          AWAITS_DRIVER_PICKUP
        </Button>
        <Button
          size="xs"
          colorScheme="brand"
          variant={status.includes('DROP_OFF') ? 'solid' : 'outline'}
          onClick={() => handleStatusChange('DROP_OFF')}>
          DROP_OFF
        </Button>
        <Button
          size="xs"
          colorScheme="brand"
          variant={status.includes('DISPOSSESSIONS') ? 'solid' : 'outline'}
          onClick={() => handleStatusChange('DISPOSSESSIONS')}>
          DISPOSSESSIONS
        </Button>
      </Flex>
      <Flex justifyContent="space-between" mb="4" alignItems="center" gap="8">
        <Text fontSize="sm" color="gray.500">
          DDO: Driver Drop Off; CP: Consignee Pickup; AP: Aged Parcel; SD: Shipper Drop Off
        </Text>
        <Flex justifyContent="flex-end" gap="4">
          <Button size="sm" colorScheme="brand" onClick={handleManualSync}>
            Manueller Sync
          </Button>
          <Button size="sm" colorScheme="brand" variant="outline" onClick={handleRefresh}>
            Aktualisieren
          </Button>
        </Flex>
      </Flex>
      {loading ? (
        <Flex width="100%" height="300px" justifyContent="center" alignItems="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <Flex width="100%" height="300px" justifyContent="center" alignItems="center">
          Ein Fehler ist aufgetreten. Schreib Osamah an.
        </Flex>
      ) : (
        <TableContainer>
          <Table variant="striped" size="sm">
            <Thead>
              <Tr>
                <Th>Tracking Number</Th>
                <Th>Status & Infos</Th>
                <Th>Addressat</Th>
                <Th>1. Sync am</Th>
                <Th>Letztes Update</Th>
                <Th>DDO Scan At</Th>
                <Th>DDO Scan Success</Th>
                <Th>CP Scan At</Th>
                <Th>CP Scan Success</Th>
                <Th>Aged At</Th>
                <Th>AP Scan At</Th>
                <Th>AP Scan Success</Th>
                <Th>SD Scan At</Th>
                <Th>SD Scan Success</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {[...data.internalAllUPSParcels.parcels]
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .map((parcel) => (
                  <Tr key={parcel.trackingNumber}>
                    <Td>{parcel.trackingNumber}</Td>
                    <Td>
                      <Flex flexWrap="wrap" gap="2">
                        <Tooltip label={parcel.deliveryStatus} hasArrow>
                          <Badge colorScheme={statusColorScheme[parcel.deliveryStatus]}>{parcelStatusText(parcel.deliveryStatus)}</Badge>
                        </Tooltip>
                        {parcel.notInInventorySync && parcel.deliveryStatus !== 'DISPOSSESSIONS' ? (
                          <Badge colorScheme="red">Nicht mehr im Inventar</Badge>
                        ) : null}
                        {parcel.hiddenInApp && <Badge colorScheme="orange">Ausgeblendet</Badge>}
                      </Flex>
                      {parcel.epraIndicator || parcel.addresseeOnlyIndicator || parcel.adultSignatureIndicator ? (
                        <Flex flexWrap="wrap" gap="2" mt="2">
                          {parcel.epraIndicator && <Badge colorScheme="blue">Hat EPRA PIN</Badge>}
                          {parcel.addresseeOnlyIndicator && <Badge colorScheme="blue">Abholung nur durch Addressat</Badge>}
                          {parcel.adultSignatureIndicator && <Badge colorScheme="blue">Abholung durch Erwachsene</Badge>}
                        </Flex>
                      ) : null}
                    </Td>
                    <Td>{parcel.consigneeName}</Td>
                    <Td>{formatUTCToLocal(parcel.createdAt)}</Td>
                    <Td>{formatUTCToLocal(parcel.updatedAt)}</Td>
                    <Td>{formatUTCToLocal(parcel.driverDropOffAt) || '---'}</Td>
                    <Td>{formatUTCToLocal(parcel.driverDropOffAckAt) || '---'}</Td>
                    <Td>{formatUTCToLocal(parcel.consigneePickupAt) || '---'}</Td>
                    <Td>{formatUTCToLocal(parcel.consigneePickupAckAt) || '---'}</Td>
                    <Td>{formatUTCToLocal(parcel.parcelAgedAt) || '---'}</Td>
                    <Td>{formatUTCToLocal(parcel.agedParcelScanAt) || '---'}</Td>
                    <Td>{formatUTCToLocal(parcel.agedParcelScanAckAt) || '---'}</Td>
                    <Td>{formatUTCToLocal(parcel.shipperDropOffAt) || '---'}</Td>
                    <Td>{formatUTCToLocal(parcel.shipperDropOffAckAt) || '---'}</Td>
                    <Td>
                      <Tooltip label={parcel.hiddenInApp ? 'Paket in der App einblenden' : 'Paket in der App ausblenden'} hasArrow>
                        <Button
                          size="xs"
                          colorScheme={parcel.hiddenInApp ? 'green' : 'orange'}
                          variant="outline"
                          onClick={async () => {
                            try {
                              await setParcelHiddenInApp({
                                variables: {
                                  accessPointID: parcel.accessPointID,
                                  trackingNumber: parcel.trackingNumber,
                                  hidden: !parcel.hiddenInApp,
                                  internalUserId: user.id,
                                },
                              });
                              toast({
                                title: 'Erfolg!',
                                description: parcel.hiddenInApp ? 'Paket wurde eingeblendet.' : 'Paket wurde ausgeblendet.',
                                status: 'success',
                              });
                              refetch();
                            } catch (error) {
                              toast({
                                description: 'Ein Fehler ist aufgetreten!',
                                status: 'error',
                              });
                            }
                          }}>
                          {parcel.hiddenInApp ? 'Einblenden' : 'Ausblenden'}
                        </Button>
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
